/**
 * FrameworkActivationWizard — 4-step dialog for activating a compliance framework
 *
 * Steps:
 *  1. Overview       — what the framework covers, authority, control count, retention
 *  2. Scope          — which AI systems / data classes / environments this applies to
 *  3. Assignments    — framework owner, authorizing official, assessor (if applicable)
 *  4. Review         — confirmation summary with legal attestation checkbox
 *
 * This is the flow a tenant admin (IT lead or CISO) uses to formally turn a
 * framework ON. It writes setupAnswers + activatedAt + activatedByUserId + enabled=true.
 *
 * Mock-only: persistence is simulated with a callback. In production this would
 * POST to /api/v1/frameworks/:id/activate and trigger control-seed jobs.
 */

import { useState, useMemo } from 'react';
import {
  ShieldCheck, ArrowRight, ArrowLeft, Check, AlertTriangle, Info,
  FileText, Users, Target, CheckCircle2, Lock,
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import type { Framework } from '@/lib/mock-trust-data';
import { useTrustEntities, shortId } from '@/lib/use-trust-entities';
import { useTrustAiSystems } from '@/lib/use-trust-ai-systems';

// ─── Step definition ─────────────────────────────────────────────────────────

type WizardStep = 'overview' | 'scope' | 'assignments' | 'review';

const STEPS: Array<{ id: WizardStep; label: string; icon: any; description: string }> = [
  { id: 'overview',    label: 'Overview',    icon: Info,          description: 'Review what this framework requires' },
  { id: 'scope',       label: 'Scope',       icon: Target,        description: 'Define what is in scope' },
  { id: 'assignments', label: 'Assignments', icon: Users,         description: 'Assign responsibility' },
  { id: 'review',      label: 'Review',      icon: CheckCircle2,  description: 'Confirm and activate' },
];

// Tenant users + AI systems are now sourced from real APIs (see hooks
// inside the component). The legacy hardcoded fallbacks below were the
// founder-flagged mock data (Sarah Chen / Mike Rodriguez / Priya Patel /
// James Kim / Lisa Wong + ai-ask-argus etc.) and have been removed.

const DATA_CLASSES = [
  { id: 'public',       label: 'Public',        description: 'Marketing content, published docs' },
  { id: 'internal',     label: 'Internal',      description: 'Employee-only business data' },
  { id: 'confidential', label: 'Confidential',  description: 'Trade secrets, customer data' },
  { id: 'cui',          label: 'CUI',           description: 'Controlled Unclassified Information' },
  { id: 'itar',         label: 'ITAR',          description: 'Export-controlled technical data' },
  { id: 'phi',          label: 'PHI',           description: 'Protected Health Information' },
  { id: 'pii',          label: 'PII',           description: 'Personally Identifiable Information' },
];

const ENVIRONMENTS = [
  { id: 'production', label: 'Production' },
  { id: 'staging',    label: 'Staging' },
  { id: 'dev',        label: 'Development' },
];

// ─── Component ───────────────────────────────────────────────────────────────

interface FrameworkActivationWizardProps {
  framework: Framework | null;
  onClose: () => void;
  onActivate: (payload: ActivationPayload) => void;
}

export interface ActivationPayload {
  frameworkId: string;
  scopeStatement: string;
  systemsInScope: string[];
  dataClassesInScope: string[];
  environmentsInScope: string[];
  ownerUserId: string;
  authorizingOfficial: string;
  assessorFirm: string;
  targetAssessmentDate: string;
  assessmentType: 'self_attest' | 'third_party' | 'certified';
  legalAttestation: boolean;
}

export function FrameworkActivationWizard({ framework, onClose, onActivate }: FrameworkActivationWizardProps) {
  const [step, setStep] = useState<WizardStep>('overview');

  // Real-data: tenant users + AI systems inventory
  const entities = useTrustEntities();
  const { data: aiSystemsData = [] } = useTrustAiSystems();
  const TENANT_USERS = entities.users;
  const AI_SYSTEMS = aiSystemsData.map(s => ({
    id: s.id,
    label: s.name,
    type: s.type,
  }));

  // Step 2 — Scope
  const [scopeStatement, setScopeStatement] = useState('');
  const [systemsInScope, setSystemsInScope] = useState<string[]>([]);
  const [dataClassesInScope, setDataClassesInScope] = useState<string[]>([]);
  const [environmentsInScope, setEnvironmentsInScope] = useState<string[]>(['production']);

  // Step 3 — Assignments
  const [ownerUserId, setOwnerUserId] = useState('');
  const [authorizingOfficial, setAuthorizingOfficial] = useState('');
  const [assessorFirm, setAssessorFirm] = useState('');
  const [targetAssessmentDate, setTargetAssessmentDate] = useState('');
  const [assessmentType, setAssessmentType] = useState<'self_attest' | 'third_party' | 'certified'>('third_party');

  // Step 4 — Review
  const [legalAttestation, setLegalAttestation] = useState(false);

  const currentStepIndex = STEPS.findIndex(s => s.id === step);
  const isFirst = currentStepIndex === 0;
  const isLast = currentStepIndex === STEPS.length - 1;

  const canProceed = useMemo(() => {
    if (step === 'overview') return true;
    if (step === 'scope') {
      return scopeStatement.trim().length >= 10 && systemsInScope.length > 0 && dataClassesInScope.length > 0 && environmentsInScope.length > 0;
    }
    if (step === 'assignments') {
      return ownerUserId && authorizingOfficial.trim() && targetAssessmentDate;
    }
    if (step === 'review') {
      return legalAttestation;
    }
    return false;
  }, [step, scopeStatement, systemsInScope, dataClassesInScope, environmentsInScope, ownerUserId, authorizingOfficial, targetAssessmentDate, legalAttestation]);

  const handleNext = () => {
    if (!canProceed) return;
    if (isLast) {
      if (!framework) return;
      onActivate({
        frameworkId: framework.id,
        scopeStatement,
        systemsInScope,
        dataClassesInScope,
        environmentsInScope,
        ownerUserId,
        authorizingOfficial,
        assessorFirm,
        targetAssessmentDate,
        assessmentType,
        legalAttestation,
      });
      toast.success(`${framework.shortName} activated`, {
        description: `${framework.controlCount} controls seeded. Setup will continue in the framework workspace.`,
      });
      handleReset();
      return;
    }
    setStep(STEPS[currentStepIndex + 1].id);
  };

  const handleBack = () => {
    if (isFirst) return;
    setStep(STEPS[currentStepIndex - 1].id);
  };

  const handleReset = () => {
    setStep('overview');
    setScopeStatement('');
    setSystemsInScope(['ai-ask-argus']);
    setDataClassesInScope([]);
    setEnvironmentsInScope(['production']);
    setOwnerUserId('');
    setAuthorizingOfficial('');
    setAssessorFirm('');
    setTargetAssessmentDate('');
    setAssessmentType('third_party');
    setLegalAttestation(false);
    onClose();
  };

  const toggleMulti = (setter: React.Dispatch<React.SetStateAction<string[]>>, id: string) => {
    setter(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  if (!framework) return null;

  return (
    <Dialog open={!!framework} onOpenChange={(o) => !o && handleReset()}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-hidden flex flex-col p-0 gap-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b">
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className={cn('h-5 w-5', `text-${framework.iconColor}-600 dark:text-${framework.iconColor}-400`)} />
            Activate {framework.shortName}
          </DialogTitle>
          <DialogDescription>{framework.fullName} — {framework.authority}</DialogDescription>
        </DialogHeader>

        {/* Stepper */}
        <div className="px-6 py-4 border-b bg-muted/30">
          <div className="flex items-center justify-between gap-2">
            {STEPS.map((s, idx) => {
              const Icon = s.icon;
              const done = idx < currentStepIndex;
              const active = idx === currentStepIndex;
              return (
                <div key={s.id} className="flex items-center gap-2 flex-1">
                  <div className={cn(
                    'flex items-center gap-2 rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
                    active && 'bg-primary text-primary-foreground',
                    done && 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300',
                    !active && !done && 'text-muted-foreground',
                  )}>
                    {done ? <Check className="h-3.5 w-3.5" /> : <Icon className="h-3.5 w-3.5" />}
                    {s.label}
                  </div>
                  {idx < STEPS.length - 1 && (
                    <div className={cn('flex-1 h-[1px]', done ? 'bg-emerald-300 dark:bg-emerald-700' : 'bg-border')} />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto min-h-0">
          <div className="px-6 py-5">
            {step === 'overview' && <OverviewStep framework={framework} />}
            {step === 'scope' && (
              <ScopeStep
                framework={framework}
                scopeStatement={scopeStatement}
                setScopeStatement={setScopeStatement}
                systemsInScope={systemsInScope}
                toggleSystem={(id) => toggleMulti(setSystemsInScope, id)}
                dataClassesInScope={dataClassesInScope}
                toggleDataClass={(id) => toggleMulti(setDataClassesInScope, id)}
                environmentsInScope={environmentsInScope}
                toggleEnvironment={(id) => toggleMulti(setEnvironmentsInScope, id)}
                aiSystems={AI_SYSTEMS}
              />
            )}
            {step === 'assignments' && (
              <AssignmentsStep
                ownerUserId={ownerUserId}
                setOwnerUserId={setOwnerUserId}
                authorizingOfficial={authorizingOfficial}
                setAuthorizingOfficial={setAuthorizingOfficial}
                assessorFirm={assessorFirm}
                setAssessorFirm={setAssessorFirm}
                targetAssessmentDate={targetAssessmentDate}
                setTargetAssessmentDate={setTargetAssessmentDate}
                assessmentType={assessmentType}
                setAssessmentType={setAssessmentType}
                tenantUsers={TENANT_USERS}
              />
            )}
            {step === 'review' && (
              <ReviewStep
                framework={framework}
                scopeStatement={scopeStatement}
                systemsInScope={systemsInScope}
                dataClassesInScope={dataClassesInScope}
                environmentsInScope={environmentsInScope}
                ownerUserId={ownerUserId}
                authorizingOfficial={authorizingOfficial}
                assessorFirm={assessorFirm}
                targetAssessmentDate={targetAssessmentDate}
                assessmentType={assessmentType}
                legalAttestation={legalAttestation}
                setLegalAttestation={setLegalAttestation}
                tenantUsers={TENANT_USERS}
                aiSystems={AI_SYSTEMS}
              />
            )}
          </div>
        </div>

        <DialogFooter className="px-6 py-4 border-t bg-muted/20">
          <div className="flex items-center justify-between w-full">
            <div className="text-xs text-muted-foreground">
              Step {currentStepIndex + 1} of {STEPS.length} — {STEPS[currentStepIndex].description}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" onClick={handleReset}>Cancel</Button>
              {!isFirst && (
                <Button variant="outline" onClick={handleBack}>
                  <ArrowLeft className="h-4 w-4 mr-1" /> Back
                </Button>
              )}
              <Button onClick={handleNext} disabled={!canProceed}>
                {isLast ? (
                  <>
                    <ShieldCheck className="h-4 w-4 mr-1" /> Activate Framework
                  </>
                ) : (
                  <>
                    Next <ArrowRight className="h-4 w-4 ml-1" />
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Step 1: Overview ────────────────────────────────────────────────────────

function OverviewStep({ framework }: { framework: Framework }) {
  return (
    <div className="space-y-5">
      <div>
        <div className="flex items-center gap-2 mb-2">
          <Badge variant="outline" className="text-[10px] capitalize">{framework.category.replace(/_/g, ' ')}</Badge>
          <Badge variant="outline" className="text-[10px]">{framework.jurisdiction}</Badge>
          <Badge variant="outline" className="text-[10px]">{framework.controlCount} controls</Badge>
          <Badge variant="outline" className="text-[10px] capitalize">{framework.updateFrequency}</Badge>
        </div>
        <p className="text-sm text-muted-foreground">{framework.description}</p>
      </div>

      <Separator />

      <div>
        <h4 className="text-sm font-semibold mb-2">What activation does</h4>
        <ul className="space-y-2 text-xs text-muted-foreground">
          <li className="flex items-start gap-2">
            <Check className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
            <span>Seeds <strong className="text-foreground">{framework.controlCount} control records</strong> into your compliance workspace with default implementation guidance.</span>
          </li>
          <li className="flex items-start gap-2">
            <Check className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
            <span>Enables <strong className="text-foreground">cross-module tagging</strong> — agents, documents, and data flows can be tagged with controls from this framework.</span>
          </li>
          <li className="flex items-start gap-2">
            <Check className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
            <span>Starts <strong className="text-foreground">evidence collection</strong> for continuous control monitoring. New evidence is auto-linked when relevant.</span>
          </li>
          <li className="flex items-start gap-2">
            <Check className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
            <span>Applies <strong className="text-foreground">{framework.retentionYears}-year audit retention</strong> to all related audit log entries.</span>
          </li>
        </ul>
      </div>

      <div className="rounded-lg border bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-900 p-3">
        <div className="flex gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
          <div className="text-xs">
            <p className="font-semibold text-amber-900 dark:text-amber-200">Activation is a governance action</p>
            <p className="text-amber-800 dark:text-amber-300 mt-1">
              Once activated, turning off this framework requires executive approval and a deactivation audit trail.
              You will be asked to legally attest on the final step.
            </p>
          </div>
        </div>
      </div>

      <Separator />

      <div className="grid grid-cols-2 gap-4 text-xs">
        <div>
          <div className="text-muted-foreground uppercase tracking-wider text-[10px] font-semibold mb-1">Authority</div>
          <div className="font-medium">{framework.authority}</div>
        </div>
        <div>
          <div className="text-muted-foreground uppercase tracking-wider text-[10px] font-semibold mb-1">Enforcement Date</div>
          <div className="font-medium">{framework.enforcementDate ?? 'Already enforced'}</div>
        </div>
      </div>
    </div>
  );
}

// ─── Step 2: Scope ───────────────────────────────────────────────────────────

function ScopeStep({
  framework,
  scopeStatement, setScopeStatement,
  systemsInScope, toggleSystem,
  dataClassesInScope, toggleDataClass,
  environmentsInScope, toggleEnvironment,
  aiSystems,
}: {
  framework: Framework;
  scopeStatement: string; setScopeStatement: (v: string) => void;
  systemsInScope: string[]; toggleSystem: (id: string) => void;
  dataClassesInScope: string[]; toggleDataClass: (id: string) => void;
  environmentsInScope: string[]; toggleEnvironment: (id: string) => void;
  aiSystems: Array<{ id: string; label: string; type: string }>;
}) {
  const AI_SYSTEMS = aiSystems;
  return (
    <div className="space-y-5">
      <div>
        <h4 className="text-sm font-semibold mb-1">Define {framework.shortName} scope</h4>
        <p className="text-xs text-muted-foreground">
          Scope determines which AI systems, data classes, and environments are assessed.
          This becomes your System Security Plan / Scope Statement.
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="scope-statement" className="text-xs font-semibold">
          Scope statement <span className="text-red-500">*</span>
        </Label>
        <Textarea
          id="scope-statement"
          value={scopeStatement}
          onChange={(e) => setScopeStatement(e.target.value)}
          placeholder={`Example: "The ${framework.shortName} assessment boundary includes the ArgusAI platform running in our primary datacenter, all production AI agents that process operational data, and all users in the Engineering and Operations departments."`}
          rows={4}
          className="text-xs"
        />
        <p className="text-[10px] text-muted-foreground">{scopeStatement.length} chars (min 10)</p>
      </div>

      <Separator />

      <div className="space-y-2">
        <Label className="text-xs font-semibold">
          AI Systems in scope <span className="text-red-500">*</span>
        </Label>
        <p className="text-[10px] text-muted-foreground">
          Which AI systems this framework governs. Events from these systems will be tagged.
        </p>
        <div className="space-y-1.5">
          {AI_SYSTEMS.length === 0 && (
            <div className="rounded-md border border-dashed p-3 text-[11px] text-muted-foreground">
              No AI systems registered yet. Add systems in Trust → AI Systems Inventory before activating a framework.
            </div>
          )}
          {AI_SYSTEMS.map(sys => (
            <label
              key={sys.id}
              className={cn(
                'flex items-center gap-2 rounded-md border p-2 cursor-pointer transition-colors',
                systemsInScope.includes(sys.id) ? 'bg-primary/5 border-primary/30' : 'hover:bg-muted/50',
              )}
            >
              <Checkbox
                checked={systemsInScope.includes(sys.id)}
                onCheckedChange={() => toggleSystem(sys.id)}
              />
              <div className="flex-1">
                <div className="text-xs font-medium">{sys.label}</div>
                <div className="text-[10px] text-muted-foreground">{sys.type}</div>
              </div>
            </label>
          ))}
        </div>
      </div>

      <Separator />

      <div className="space-y-2">
        <Label className="text-xs font-semibold">
          Data classes in scope <span className="text-red-500">*</span>
        </Label>
        <p className="text-[10px] text-muted-foreground">
          Which data classifications this framework applies to.
        </p>
        <div className="grid grid-cols-2 gap-1.5">
          {DATA_CLASSES.map(dc => (
            <label
              key={dc.id}
              className={cn(
                'flex items-center gap-2 rounded-md border p-2 cursor-pointer transition-colors',
                dataClassesInScope.includes(dc.id) ? 'bg-primary/5 border-primary/30' : 'hover:bg-muted/50',
              )}
            >
              <Checkbox
                checked={dataClassesInScope.includes(dc.id)}
                onCheckedChange={() => toggleDataClass(dc.id)}
              />
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium">{dc.label}</div>
                <div className="text-[10px] text-muted-foreground truncate">{dc.description}</div>
              </div>
            </label>
          ))}
        </div>
      </div>

      <Separator />

      <div className="space-y-2">
        <Label className="text-xs font-semibold">
          Environments in scope <span className="text-red-500">*</span>
        </Label>
        <div className="flex gap-2">
          {ENVIRONMENTS.map(env => (
            <label
              key={env.id}
              className={cn(
                'flex items-center gap-2 rounded-md border px-3 py-2 cursor-pointer transition-colors',
                environmentsInScope.includes(env.id) ? 'bg-primary/5 border-primary/30' : 'hover:bg-muted/50',
              )}
            >
              <Checkbox
                checked={environmentsInScope.includes(env.id)}
                onCheckedChange={() => toggleEnvironment(env.id)}
              />
              <span className="text-xs font-medium">{env.label}</span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Step 3: Assignments ─────────────────────────────────────────────────────

function AssignmentsStep({
  ownerUserId, setOwnerUserId,
  authorizingOfficial, setAuthorizingOfficial,
  assessorFirm, setAssessorFirm,
  targetAssessmentDate, setTargetAssessmentDate,
  assessmentType, setAssessmentType,
  tenantUsers,
}: {
  ownerUserId: string; setOwnerUserId: (v: string) => void;
  authorizingOfficial: string; setAuthorizingOfficial: (v: string) => void;
  assessorFirm: string; setAssessorFirm: (v: string) => void;
  targetAssessmentDate: string; setTargetAssessmentDate: (v: string) => void;
  assessmentType: 'self_attest' | 'third_party' | 'certified';
  setAssessmentType: (v: 'self_attest' | 'third_party' | 'certified') => void;
  tenantUsers: Array<{ id: string; name: string; email: string; role: string }>;
}) {
  const TENANT_USERS = tenantUsers;
  return (
    <div className="space-y-5">
      <div>
        <h4 className="text-sm font-semibold mb-1">Assign responsibility</h4>
        <p className="text-xs text-muted-foreground">
          Every framework needs a named owner, authorizing official, and assessment plan.
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="owner" className="text-xs font-semibold">
          Framework Owner <span className="text-red-500">*</span>
        </Label>
        <p className="text-[10px] text-muted-foreground">
          The person accountable for maintaining this framework's controls.
        </p>
        <Select value={ownerUserId} onValueChange={setOwnerUserId}>
          <SelectTrigger id="owner" className="h-9 text-xs">
            <SelectValue placeholder="Select framework owner..." />
          </SelectTrigger>
          <SelectContent>
            {TENANT_USERS.length === 0 && (
              <div className="px-2 py-1.5 text-[11px] text-muted-foreground">No users available — invite teammates from Users.</div>
            )}
            {TENANT_USERS.map(u => (
              <SelectItem key={u.id} value={u.id} className="text-xs">
                <div>
                  <div className="font-medium">{u.name}</div>
                  <div className="text-[10px] text-muted-foreground">{u.role} · {u.email}</div>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="authorizing" className="text-xs font-semibold">
          Authorizing Official <span className="text-red-500">*</span>
        </Label>
        <p className="text-[10px] text-muted-foreground">
          The senior executive who legally authorizes this framework's scope (often CEO, CISO, or CCO).
        </p>
        <Input
          id="authorizing"
          value={authorizingOfficial}
          onChange={(e) => setAuthorizingOfficial(e.target.value)}
          placeholder="e.g. CISO or VP of Compliance"
          className="h-9 text-xs"
        />
      </div>

      <Separator />

      <div className="space-y-2">
        <Label className="text-xs font-semibold">Assessment type</Label>
        <RadioGroup
          value={assessmentType}
          onValueChange={(v) => setAssessmentType(v as 'self_attest' | 'third_party' | 'certified')}
          className="space-y-2"
        >
          <label className={cn(
            'flex items-start gap-2 rounded-md border p-3 cursor-pointer transition-colors',
            assessmentType === 'self_attest' ? 'bg-primary/5 border-primary/30' : 'hover:bg-muted/50',
          )}>
            <RadioGroupItem value="self_attest" className="mt-0.5" />
            <div className="flex-1">
              <div className="text-xs font-medium">Self-attestation</div>
              <div className="text-[10px] text-muted-foreground">Internal assessment only. Fastest path. Valid for SOC 2 Type I prep, ISO gap analysis.</div>
            </div>
          </label>
          <label className={cn(
            'flex items-start gap-2 rounded-md border p-3 cursor-pointer transition-colors',
            assessmentType === 'third_party' ? 'bg-primary/5 border-primary/30' : 'hover:bg-muted/50',
          )}>
            <RadioGroupItem value="third_party" className="mt-0.5" />
            <div className="flex-1">
              <div className="text-xs font-medium">Third-party assessor</div>
              <div className="text-[10px] text-muted-foreground">Engaged firm will conduct assessment. Required for SOC 2 Type II, ISO 27001, CMMC L2 (C3PAO).</div>
            </div>
          </label>
          <label className={cn(
            'flex items-start gap-2 rounded-md border p-3 cursor-pointer transition-colors',
            assessmentType === 'certified' ? 'bg-primary/5 border-primary/30' : 'hover:bg-muted/50',
          )}>
            <RadioGroupItem value="certified" className="mt-0.5" />
            <div className="flex-1">
              <div className="text-xs font-medium">Accredited certification body</div>
              <div className="text-[10px] text-muted-foreground">Formal certification required (e.g. ANAB-accredited for ISO 42001, DoD CMMC AB-certified).</div>
            </div>
          </label>
        </RadioGroup>
      </div>

      <div className="space-y-2">
        <Label htmlFor="assessor" className="text-xs font-semibold">
          Assessor firm <span className="text-muted-foreground font-normal">(optional)</span>
        </Label>
        <Input
          id="assessor"
          value={assessorFirm}
          onChange={(e) => setAssessorFirm(e.target.value)}
          placeholder="e.g. Schellman & Co., A-LIGN, Coalfire"
          className="h-9 text-xs"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="target-date" className="text-xs font-semibold">
          Target assessment date <span className="text-red-500">*</span>
        </Label>
        <p className="text-[10px] text-muted-foreground">
          When do you plan to complete the first formal assessment? Typically 90-180 days out.
        </p>
        <Input
          id="target-date"
          type="date"
          value={targetAssessmentDate}
          onChange={(e) => setTargetAssessmentDate(e.target.value)}
          className="h-9 text-xs w-48"
          min={new Date().toISOString().slice(0, 10)}
        />
      </div>
    </div>
  );
}

// ─── Step 4: Review ──────────────────────────────────────────────────────────

function ReviewStep({
  framework,
  scopeStatement,
  systemsInScope, dataClassesInScope, environmentsInScope,
  ownerUserId, authorizingOfficial, assessorFirm, targetAssessmentDate, assessmentType,
  legalAttestation, setLegalAttestation,
  tenantUsers, aiSystems,
}: {
  framework: Framework;
  scopeStatement: string;
  systemsInScope: string[];
  dataClassesInScope: string[];
  environmentsInScope: string[];
  ownerUserId: string;
  authorizingOfficial: string;
  assessorFirm: string;
  targetAssessmentDate: string;
  assessmentType: 'self_attest' | 'third_party' | 'certified';
  legalAttestation: boolean;
  setLegalAttestation: (v: boolean) => void;
  tenantUsers: Array<{ id: string; name: string; email: string; role: string }>;
  aiSystems: Array<{ id: string; label: string; type: string }>;
}) {
  const owner = tenantUsers.find(u => u.id === ownerUserId);
  const systemLabels = systemsInScope.map(id => aiSystems.find(s => s.id === id)?.label ?? id);
  const classLabels = dataClassesInScope.map(id => DATA_CLASSES.find(d => d.id === id)?.label ?? id);
  const envLabels = environmentsInScope.map(id => ENVIRONMENTS.find(e => e.id === id)?.label ?? id);
  const typeLabels: Record<string, string> = {
    self_attest: 'Self-attestation',
    third_party: 'Third-party assessor',
    certified: 'Accredited certification body',
  };

  return (
    <div className="space-y-5">
      <div>
        <h4 className="text-sm font-semibold mb-1">Review activation</h4>
        <p className="text-xs text-muted-foreground">
          Confirm the details below. On activation, {framework.controlCount} controls will be seeded.
        </p>
      </div>

      <div className="space-y-3">
        <ReviewRow label="Framework" value={
          <div className="flex items-center gap-2">
            <ShieldCheck className={cn('h-4 w-4', `text-${framework.iconColor}-600 dark:text-${framework.iconColor}-400`)} />
            <span className="font-medium">{framework.fullName}</span>
          </div>
        } />
        <ReviewRow label="Authority" value={framework.authority} />
        <ReviewRow label="Controls" value={`${framework.controlCount} will be seeded`} />
        <ReviewRow label="Retention" value={`${framework.retentionYears} years`} />

        <Separator />

        <ReviewRow label="Scope statement" value={
          <div className="text-[11px] leading-relaxed whitespace-pre-wrap">{scopeStatement}</div>
        } multiline />
        <ReviewRow label="AI Systems" value={
          <div className="flex flex-wrap gap-1">
            {systemLabels.map(l => <Badge key={l} variant="outline" className="text-[10px]">{l}</Badge>)}
          </div>
        } />
        <ReviewRow label="Data classes" value={
          <div className="flex flex-wrap gap-1">
            {classLabels.map(l => <Badge key={l} variant="outline" className="text-[10px]">{l}</Badge>)}
          </div>
        } />
        <ReviewRow label="Environments" value={
          <div className="flex flex-wrap gap-1">
            {envLabels.map(l => <Badge key={l} variant="outline" className="text-[10px]">{l}</Badge>)}
          </div>
        } />

        <Separator />

        <ReviewRow label="Owner" value={owner ? `${owner.name} (${owner.role})` : '—'} />
        <ReviewRow label="Authorizing Official" value={authorizingOfficial} />
        <ReviewRow label="Assessment type" value={typeLabels[assessmentType]} />
        {assessorFirm && <ReviewRow label="Assessor firm" value={assessorFirm} />}
        <ReviewRow label="Target date" value={targetAssessmentDate ? new Date(targetAssessmentDate).toLocaleDateString() : '—'} />
      </div>

      <Separator />

      <div className="rounded-lg border-2 border-amber-300 dark:border-amber-900 bg-amber-50 dark:bg-amber-950/30 p-4 space-y-3">
        <div className="flex items-start gap-2">
          <Lock className="h-4 w-4 text-amber-700 dark:text-amber-400 shrink-0 mt-0.5" />
          <div className="text-xs">
            <p className="font-semibold text-amber-900 dark:text-amber-200">Legal attestation required</p>
            <p className="text-amber-800 dark:text-amber-300 mt-1">
              By activating {framework.shortName}, you attest on behalf of your organization that:
            </p>
          </div>
        </div>
        <ul className="space-y-1 text-[11px] text-amber-900 dark:text-amber-200 list-disc pl-6">
          <li>The authorizing official named above has approved this scope.</li>
          <li>The defined scope accurately represents systems and data in this framework's boundary.</li>
          <li>Your organization has the legal authority to be assessed against this framework.</li>
          <li>Future changes to scope will be tracked in the audit log and require re-authorization.</li>
        </ul>
        <label className="flex items-start gap-2 cursor-pointer pt-2 border-t border-amber-200 dark:border-amber-900">
          <Checkbox
            checked={legalAttestation}
            onCheckedChange={(c) => setLegalAttestation(c === true)}
            className="mt-0.5"
          />
          <span className="text-xs font-medium text-amber-900 dark:text-amber-200">
            I attest on behalf of my organization and authorize the activation of {framework.shortName}.
          </span>
        </label>
      </div>
    </div>
  );
}

function ReviewRow({ label, value, multiline = false }: { label: string; value: React.ReactNode; multiline?: boolean }) {
  return (
    <div className={cn('grid gap-2', multiline ? 'grid-cols-1' : 'grid-cols-[140px_1fr] items-start')}>
      <div className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold pt-0.5">{label}</div>
      <div className="text-xs">{value}</div>
    </div>
  );
}
